/**
 * Alert History Panel (DC-194, moved for DC-1648)
 *
 * Slide-out panel showing governance assignment alert history with
 * summary stats, severity filtering, and relative timestamps. Lives in the
 * FAIR persona topology view; styling is token-driven via
 * persona-topology.css.
 */

import React, { useState, useEffect } from 'react';
import type { AlertHistoryEntry, AlertSummary } from './types';

interface AlertHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

export const AlertHistoryPanel: React.FC<AlertHistoryPanelProps> = ({ isOpen, onClose, authFetch }) => {
  const [alerts, setAlerts] = useState<AlertHistoryEntry[]>([]);
  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'errors' | 'warnings'>('all');
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Fetch alerts when panel opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const [alertsRes, summaryRes] = await Promise.all([
          authFetch('/api/governance/alerts?limit=50'),
          authFetch('/api/governance/alerts/summary'),
        ]);

        if (alertsRes.ok) {
          const data = await alertsRes.json();
          setAlerts(data.alerts || []);
        }

        if (summaryRes.ok) {
          const data = await summaryRes.json();
          setSummary(data.summary || null);
        }
      } catch (err) {
        console.error('[AlertHistoryPanel] Failed to fetch alerts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    if (panelRef.current) {
      panelRef.current.focus();
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Filter alerts based on selected filter
  const filteredAlerts = alerts.filter((alert) => {
    if (filter === 'errors') return alert.alert_severity === 'error';
    if (filter === 'warnings') return alert.alert_severity === 'warning';
    return true;
  });

  // Format relative time
  const formatRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get severity icon
  const getSeverityIcon = (severity: string): string => {
    switch (severity) {
      case 'error': return '!';
      case 'warning': return 'W';
      default: return 'i';
    }
  };

  return (
    <div
      className="alert-history-panel"
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-labelledby="alert-history-title"
    >
      <div className="alert-panel-header">
        <h3 id="alert-history-title">Alert History</h3>
        <button className="panel-close" onClick={onClose} aria-label="Close panel">&times;</button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="alert-summary-stats">
          <div className="summary-stat">
            <span className="stat-number">{summary.last24Hours.total}</span>
            <span className="stat-label">Last 24h</span>
          </div>
          <div className="summary-stat warning">
            <span className="stat-number">{summary.last7Days.warnings}</span>
            <span className="stat-label">Warnings (7d)</span>
          </div>
          <div className="summary-stat error">
            <span className="stat-number">{summary.last7Days.errors}</span>
            <span className="stat-label">Errors (7d)</span>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="alert-filter-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={filter === 'all'}
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({alerts.length})
        </button>
        <button
          role="tab"
          aria-selected={filter === 'errors'}
          className={`filter-tab ${filter === 'errors' ? 'active' : ''}`}
          onClick={() => setFilter('errors')}
        >
          Errors ({alerts.filter(a => a.alert_severity === 'error').length})
        </button>
        <button
          role="tab"
          aria-selected={filter === 'warnings'}
          className={`filter-tab ${filter === 'warnings' ? 'active' : ''}`}
          onClick={() => setFilter('warnings')}
        >
          Warnings ({alerts.filter(a => a.alert_severity === 'warning').length})
        </button>
      </div>

      {/* Alert List */}
      <div className="alert-list" role="list">
        {loading ? (
          <div className="alert-loading">Loading alerts...</div>
        ) : filteredAlerts.length === 0 ? (
          <div className="alert-empty">
            <span className="empty-icon">B</span>
            <p>No alerts to display</p>
          </div>
        ) : (
          filteredAlerts.map((alert) => (
            <div
              key={alert.audit_id}
              className={`alert-item severity-${alert.alert_severity}`}
              role="listitem"
            >
              <div className={`alert-icon severity-${alert.alert_severity}`}>
                {getSeverityIcon(alert.alert_severity)}
              </div>
              <div className="alert-content">
                <p className="alert-message">{alert.alert_message}</p>
                <div className="alert-meta">
                  <span className="alert-time">{formatRelativeTime(alert.changed_at)}</span>
                  <span className="alert-separator">by</span>
                  <span className="alert-actor">{alert.changed_by}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* View All Link */}
      {alerts.length > 0 && (
        <div className="alert-panel-footer">
          <span className="footer-text">Showing {filteredAlerts.length} of {alerts.length} alerts</span>
        </div>
      )}
    </div>
  );
};
